/* eslint-disable */

import { qs } from './common';

/**
 * Tracking code will start running upon document.ready
 * and will start tracking bounce using the default defined internally
 *
 * Query Params:
 *  tracking - must be provided for the tracker to be loaded
 *  bounce - if provided bounce will override BOUNCE_MILLIS and would auto-launch,
 *           not waiting for the per page instance initializations
 *
 */
const initTracking = () => {
  const TEST_MODE = false;

  // config
  const SEO_TRACKER_CONFIG = {
    version: '0.0.1',
    BOUNCE_MILLIS: 2000, // threshold for determining page bounce
  };

  let config = SEO_TRACKER_CONFIG;

  // inits
  const visibilityEvents = [];

  // MAIN entrypoint, aka: document ready
  $(() => {
    _polyfillLogEvent();

    $.trackify = Object.freeze({
      init: _init(),
      config,
      visibilityEvents,
    });

    console.debug('tracker ready');
  });

  // init tracker on page-load
  function _init(conf) {
    // apply initialization constants
    config = Object.assign(SEO_TRACKER_CONFIG, conf || {});
    _launchByQuery();

    // track bounce
    if (config.BOUNCE_MILLIS > 0) {
      const callbackFunc = callbackHandler();
      pageUnloadListener(callbackFunc);
    }

    console.debug('tracker initialized');

    return () => 'tracker already initialized';
  }

  // utility function to wrap $.log_event function
  function _polyfillLogEvent() {
    if ($.log_event === undefined) {
      $.log_event = function(type, data) {
        console.debug(`Polyfilled $.log_event call was made with type: ${type}, and data: ${JSON.stringify(data)}`);
      };
    }
  }

  /**
   * This function initializes the tracker config off the query params
   * @private
   */
  function _launchByQuery() {
    let bounce = qs('bounce');

    if (bounce) {
      bounce = parseInt(bounce, 10);
      config.BOUNCE_MILLIS = bounce;
    }
  }

  /**
   * Wrapper Functions
   * */
  // a wrapper function to verify no more then a single bounce event is fired
  function callbackHandler() {
    const enableTimeOnPage = true; // location.search.includes('time_on_page');
    let firstTime = false;

    console.debug(`tracker bounce set to ${config.BOUNCE_MILLIS}`);
    if(enableTimeOnPage) {
      console.debug('tracker set to log duration on page');
    }
    /**
     * data: { eventType: [string] the browser event as the visitor left the page,
     *         duration: [number] milliseconds inwhich page had been visible
     * }
     * */
    return function(data) {
      if (!firstTime) {
        firstTime = true;

        if(enableTimeOnPage) {
          timeOnPage(data);
        } else {
          bounce(data);
        }
      }
    }
  }

  // Logs the amount of time the user spends on a page
  function timeOnPage(deta) {
    const data = {
      duration: deta.duration,
      sendBeacon: 1,
    };

    try {
      let pageType = zr.event.backendProperties.page_type.replaceAll(' ', '_').toLowerCase();
      data.field = pageType;
      data.message = `The visitor was ${data.duration} millis on ${data.field} page`;
    } catch (error) {
      console.debug('No page type provided for tracker', error);
    }

    $.log_event && $.log_event('time_on_page', data);

    const message = `Time On Page event fired $.log_event with the following data: ${JSON.stringify(data)}`;
    console.debug(message);
    return message;
  }

  // Logs if the user bounced the page
  function bounce(deta) {
    const pageBounced = deta.duration > 0 && deta.duration <= config.BOUNCE_MILLIS;

    if (pageBounced) {
      const data = {
        duration: deta.duration,
        reason: `page bounced after ${deta.duration} millis for event type ${deta.eventType}`,
        sendBeacon: 1,
      };
      try {
        let pageType = zr.event.backendProperties.page_type.replaceAll(' ', '_').toLowerCase();
        data.field = pageType;
      } catch (error) {
        console.debug('No page type provided for tracker', error);
      }

      $.log_event && $.log_event('bounce', data);

      const message = `Bounce event fired $.log_event with the following data: ${JSON.stringify(data)}`;
      console.debug(message);
      return message;
    }
  }

  /**
   * Listeners
   * */
  // listens to browser window close event and checks against a threshold whether to fire a bounce event
  function pageUnloadListener(callback) {
    // used to catch the event as early as possible in the beforeunload
    let firstTime = true;
    let reason;

    // register the start time
    visibilityEvents.push({
      timestamp: Date.now(),
      hidden: document.hidden,
    });

    // register any changes in page visibility
    document.addEventListener('visibilitychange', () => {
      visibilityEvents.push({
        timestamp: Date.now(),
        hidden: document.hidden,
      });
    }, false);

    window.addEventListener('beforeunload', (event) => {
      if (!firstTime) { return reason; }

      firstTime = false;

      // if bounce is handled in the beforeunload event the visibilityChange event of the window closing was not fired yet.
      // hence we need to push that entry manually.
      if (event.type === 'beforeunload') {
        visibilityEvents.push({
          timestamp: Date.now(),
          hidden: true,
        });
      }

      const durationSpentOnPage = _calcVisibilityDuration();

      reason = callback({
        eventType: event.type,
        duration: durationSpentOnPage,
      });

      // This gives a prompt before the window closes
      if (TEST_MODE) {
        event.preventDefault();
        event.returnValue = reason; // Chrome requires returnValue to be set
        return reason;
      }

    });
  }

  /**
   * This function runs through visibility events timestamps,
   * sums and returns the total time in milliseconds which the
   * visitor was viewing the page.
   * */
  function _calcVisibilityDuration() {
    const reducer = (accumulator, entry) => {
      const addedDuration = accumulator.prevTS > 0 && entry.hidden ? entry.timestamp - accumulator.prevTS : 0;

      return {
        duration: accumulator.duration + addedDuration,
        prevTS: entry.timestamp,
      };
    };

    return visibilityEvents.reduce(
      reducer, {
        duration: 0,
        prevTS: 0,
      },
    ).duration;
  }
};

export { initTracking };
